<template>
  <div>
    <section>
      <h3 class="text-h5 text-uppercase font-weight-extrabold mb-3">
        Account
      </h3>

      <AccountSettingsTradeUrl
        class="mb-3"
        :value="user.tradeUrl"
        :loading="loading.tradeUrl"
        @update="onTradeUrlSave"
      />

      <AccountSettingsName
        class="mb-3"
        :value="user.name"
        :loading="loading.name"
        @update="onNameSave"
      />

      <AccountSettingsEmail
        class="mb-3"
        :value="user.email"
        :verified="user.emailVerified"
        :loading="loading.email"
        @update="onEmailSave"
      />
    </section>

    <section>
      <SettingsConnections :user="user" />
    </section>

    <section>
      <SettingsLocalSettings v-model="settings" />
    </section>

    <section>
      <h3 class="text-h5 text-uppercase font-weight-extrabold mb-3">
        Fairness
      </h3>

      <p class="mb-2">
        Your server and client secret pair is what makes your games provably fair.
      </p>

      <div class="grey600 pa-4 grey100--text">
        <div class="mb-4">
          <h5 class="text-body-2 text--secondary font-weight-semibold mb-1">
            Current client seed
          </h5>
          <CopyInput :value="seedPair.client" class="grey700" />
        </div>

        <div class="mb-4">
          <h5 class="text-body-2 text--secondary font-weight-semibold mb-1">
            Current server seed (hashed)
          </h5>
          <CopyInput :value="seedPair.serverHash" class="grey700" />
        </div>

        <div>
          <h5 class="text-body-2 text--secondary font-weight-semibold mb-1">
            Amount of bets with this pair
          </h5>
          <CopyInput :value="seedPair.nonce" class="grey700" />
        </div>
      </div>

      <AccountSettingsClientSecret
        v-model="form.clientSecret"
        :loading="loading.clientSecret"
        @update="onClientSecretSave"
      />
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CopyInput from '@/components/CopyInput'
import AccountSettingsTradeUrl from './components/AccountSettingsTradeUrl'
import AccountSettingsName from './components/AccountSettingsName'
import AccountSettingsEmail from './components/AccountSettingsEmail'
import SettingsLocalSettings from './components/SettingsLocalSettings'
import AccountSettingsClientSecret from './components/AccountSettingsClientSecret'
import SettingsConnections from './components/SettingsConnections'

export default {
  components: {
    CopyInput,
    SettingsLocalSettings,
    AccountSettingsClientSecret,
    SettingsConnections,
    AccountSettingsTradeUrl,
    AccountSettingsName,
    AccountSettingsEmail,
  },
  data() {
    return {
      loading: {
        clientSecret: false,
        tradeUrl: false,
        name: false,
        email: false,
      },
      form: {
        clientSecret: null,
      },
      userSeed: null,
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
    }),
    settings: {
      get() {
        return this.$store.getters['settings/settings']
      },
      set(val) {
        this.setSettings(val)
      },
    },
    seedPair() {
      const { userSeed } = this
      return {
        serverHash: userSeed?.serverHash ?? 'n/a',
        client: userSeed?.client ?? 'n/a',
        nonce: userSeed?.nonce ?? 'n/a',
      }
    },
  },
  async created() {
    this.getActiveSeedPair()
  },
  methods: {
    ...mapMutations({
      setSettings: 'settings/setSettings',
    }),
    ...mapActions({
      _getActiveSeedPair: 'user/getActiveSeedPair',
      createSeedPair: 'user/createSeedPair',
      setTradeUrl: 'user/setTradeUrl',
      setName: 'user/setName',
      setEmail: 'user/setEmail',
    }),
    async getActiveSeedPair() {
      try {
        const userSeed = await this._getActiveSeedPair()
        this.userSeed = userSeed
      } catch (error) {
        this.$toast.error(error.message)
      }
    },
    async onClientSecretSave(secret) {
      if (!secret || this.loading.clientSecret) return
      this.loading.clientSecret = true

      try {
        const newPair = await this.createSeedPair(secret)

        this.userSeed = newPair
        this.$toast.success('Seed pair changed successfully')
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.clientSecret = false
      }
    },
    async onTradeUrlSave(url) {
      if (!url || this.loading.tradeUrl) return
      this.loading.tradeUrl = true

      try {
        await this.setTradeUrl(url)
        this.$toast.success(this.$t('page.account.settings.trade_url.toast'))
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.tradeUrl = false
      }
    },
    async onNameSave(name) {
      if (!name || this.loading.name) return
      this.loading.name = true

      try {
        await this.setName(name)
        this.$toast.success('Name updated')
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.name = false
      }
    },
    async onEmailSave(email) {
      if (this.loading.email) return
      this.loading.email = true

      try {
        const newEmail = await this.setEmail(email)
        if (newEmail) {
          this.$toast.info('A confirmation email has been sent.')
        } else if (newEmail === null) {
          this.$toast.success('Your email was removed.')
        } else {
          this.$toast.success('Your email stayed the same.')
        }
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.email = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
section:not(:last-child) {
  margin-bottom: 24px;
}
</style>
